import { Icon, Text } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { Pressable, StyleSheet, View } from 'react-native';

import DrawerPage from 'components/layout/drawer-page';
import { IQuest } from 'components/quests/loyalty-quest/types';
import { useLoyaltyQuestQuery } from 'components/quests/loyalty-quest/use-loyalty-quest-query';
import { Skeleton } from 'components/ui/skeleton';
import { LoyaltyCmsType, LoyaltyQuestStepStatus } from 'generated/graphql-gateway';
import { routes } from 'navigation/routing';
import { t } from 'state/intl/translations';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive } from 'styles/constants/primitives';

import { USER_JOURNEY_FIELDS_BY_SECTION, UserJourneyFields, UserJourneySections } from './utils';

const bell = require('./bell.svg');
const birthdayCake = require('./cake.svg');

enum SectionStatus {
  NOT_STARTED,
  STARTED,
  COMPLETED,
}

const buildSections = (challenge: IQuest) => {
  const baseSections = [
    {
      id: UserJourneySections.ProfileSetting,
      icon: birthdayCake,
      title: t('profileSettings', 'Profile Settings'),
      description: t('addBirthdayAndZipCode', 'Add Your Birthday and your Zip Code.'),
      path: routes.accountInfo,
    },
    {
      id: UserJourneySections.Communications,
      icon: bell,
      title: t('communications', 'Communications'),
      description: t(
        'turnOnCommunications',
        'Turn On Notifications, Sign Up for Emails and Text Messages.'
      ),
      path: routes.communication,
    },
  ];

  return baseSections.map(section => {
    const sectionSteps = challenge.steps.filter(
      step =>
        step.field &&
        USER_JOURNEY_FIELDS_BY_SECTION[section.id].includes(step.field as UserJourneyFields)
    );

    const earnedCrowns = sectionSteps
      .filter(step => step.status === LoyaltyQuestStepStatus.COMPLETED)
      .reduce((acm, step) => (step.bonusPoints || 0) + acm, 0);

    return {
      ...section,
      totalCrowns: sectionSteps.reduce((acm, step) => (step.bonusPoints || 0) + acm, 0),
      status: sectionSteps.every(step => step.status === LoyaltyQuestStepStatus.COMPLETED)
        ? SectionStatus.COMPLETED
        : earnedCrowns > 0
        ? SectionStatus.STARTED
        : SectionStatus.NOT_STARTED,
    };
  });
};

export const LoyaltyUserJourney = () => {
  const { navigate } = useRouter();

  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const { loadingQuests, quest } = useLoyaltyQuestQuery({
    loyaltyId,
    cmsType: LoyaltyCmsType.USER_JOURNEY,
    refecthOnFocus: true,
  });

  const showSkeleton = !loyaltyId || loadingQuests;
  const title = t('profileChallenge', 'Profile Challenge');

  return (
    <DrawerPage headerText={title} helmetTitle={title}>
      <View style={styles.container}>
        {showSkeleton && (
          <>
            <Skeleton style={styles.skeletonCard} />
            <Skeleton style={styles.skeletonCard} />
          </>
        )}
        {!showSkeleton &&
          quest &&
          buildSections(quest).map(section => {
            const completed = section.status === SectionStatus.COMPLETED;
            const started = section.status === SectionStatus.STARTED;

            const badgeByStatus = {
              [SectionStatus.NOT_STARTED]: {
                icon: 'bkCrown',
                text: t('earnAmount', {
                  crowns: section.totalCrowns,
                  defaultValue: 'Earn {crowns}',
                }),
              },
              [SectionStatus.STARTED]: {
                icon: 'infoFill',
                text: t('started', 'Started'),
              },
              [SectionStatus.COMPLETED]: {
                icon: 'bkCrown',
                text: t('amountEarned', {
                  crowns: section.totalCrowns,
                  defaultValue: '{crowns} earned',
                }),
              },
            };

            return (
              <Pressable
                key={section.title}
                style={styles.card}
                disabled={completed}
                onPress={() => navigate(section.path)}
                accessibilityRole="link"
                accessibilityLabel={section.title}
              >
                <View style={[styles.imageContainer, completed && styles.imageContainerCompleted]}>
                  <Image
                    source={section.icon}
                    tintColor={completed ? primitive.bk.$bbqBrown : primitive.$black}
                    style={[styles.image, completed && styles.imageCompleted]}
                  />
                </View>
                <View style={styles.content}>
                  <Text fontFamily="heading" style={styles.title}>
                    {section.title}
                  </Text>
                  <Text style={styles.description}>{section.description}</Text>
                  {section.totalCrowns > 0 && (
                    <View
                      style={[
                        styles.badge,
                        completed && styles.badgeCompleted,
                        started && styles.badgeStarted,
                      ]}
                    >
                      <Icon variant={badgeByStatus[section.status].icon} style={styles.badgeIcon} />
                      <Text style={styles.badgeText}>{badgeByStatus[section.status].text}</Text>
                    </View>
                  )}
                </View>
                <Icon
                  variant={completed ? 'check' : 'next'}
                  style={[styles.rightIcon, completed && styles.rightIconCompleted]}
                  color={completed ? primitive.$white : primitive.bk.$bbqBrown}
                />
              </Pressable>
            );
          })}
      </View>
    </DrawerPage>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: primitive.spacing.$4,
    gap: primitive.spacing.$3,
  },
  skeletonCard: {
    width: '100%',
    height: 110,
    margin: 0,
  },
  card: {
    flexDirection: 'row',
    backgroundColor: primitive.$white,
    borderRadius: 12,
    padding: primitive.spacing.$3,
    gap: primitive.spacing.$3,
    alignItems: 'center',
  },
  image: {
    width: 32,
    height: 32,
    opacity: 0.5,
  },
  imageCompleted: {
    opacity: 1,
  },
  imageContainer: {
    backgroundColor: primitive.$blackOpacity10,
    padding: primitive.spacing.$3,
    borderRadius: 32,
  },
  imageContainerCompleted: {
    backgroundColor: primitive.bk.$lightMustard,
  },
  content: {
    flex: 1,
    gap: primitive.spacing.$1,
  },
  title: {
    fontSize: primitive.fontSizes.md,
  },
  description: {
    fontSize: primitive.fontSizes.xs,
  },
  badge: {
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: primitive.bk.$meltyYellowOpacity20,
    alignSelf: 'flex-start',
    paddingHorizontal: primitive.spacing.$1,
    paddingVertical: 2,
    gap: primitive.spacing.$1,
  },
  badgeCompleted: {
    backgroundColor: primitive.bk.$meltyYellow,
  },
  badgeStarted: {
    backgroundColor: primitive.bk.$warningLight,
  },
  badgeIcon: {
    width: primitive.fontSizes.xs,
    height: primitive.fontSizes.xs,
    fontSize: primitive.fontSizes.xs,
    lineHeight: primitive.fontSizes.xs,
  },
  badgeText: {
    fontSize: primitive.fontSizes['2xs'],
  },
  rightIcon: {
    width: 24,
    height: 24,
    fontSize: primitive.fontSizes['3xl'],
    textAlign: 'center',
  },
  rightIconCompleted: {
    fontSize: primitive.fontSizes.md,
    backgroundColor: primitive.bk.$crunchyGreen,
    borderRadius: 24,
  },
});
